/**
 * API client functions for the AI Suite feature.
 */

import { api } from './api'

// =============================================================================
// Types
// =============================================================================

export interface AIProviderEntry {
  provider: string
  model: string | null
  enabled: boolean
  apiKeySet: boolean
  priority: number
}

export interface AIProviderConfigResponse {
  providers: AIProviderEntry[]
  activeProvider: string | null
  updatedAt: string | null
}

export interface AIActivityLogEntry {
  id: number
  studyId: string
  userId: string
  actionType: string
  meetingType: string | null
  provider: string
  model: string
  systemMessage: string | null
  prompt: string | null
  response: string | null
  status: string
  errorMessage: string | null
  tokensIn: number | null
  tokensOut: number | null
  latencyMs: number | null
  costEstimate: number | null
  outputFilePath: string | null
  createdAt: string
  completedAt: string | null
}

export interface AIActivityLogListResponse {
  data: AIActivityLogEntry[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface SlideGenerationTaskResponse {
  taskId: string
  status: string
  message: string
}

export interface SlideStatusResponse {
  taskId: string
  status: string
  filePath?: string
  error?: string
}

// =============================================================================
// Admin Config API
// =============================================================================

export async function fetchAIConfig(): Promise<AIProviderConfigResponse> {
  const response = await api.get('/admin/ai-suite/config')
  return response.data
}

export async function updateAIProvider(data: {
  provider: string
  api_key?: string
  model?: string
  enabled?: boolean
}): Promise<AIProviderConfigResponse> {
  const response = await api.put('/admin/ai-suite/config/provider', data)
  return response.data
}

export async function setActiveProvider(
  provider: string
): Promise<AIProviderConfigResponse> {
  const response = await api.put('/admin/ai-suite/config/active', { provider })
  return response.data
}

export async function deleteProviderKey(
  provider: string
): Promise<AIProviderConfigResponse> {
  const response = await api.delete(`/admin/ai-suite/config/provider/${provider}`)
  return response.data
}

export async function setProviderPriority(
  order: string[]
): Promise<AIProviderConfigResponse> {
  const response = await api.put('/admin/ai-suite/config/priority', { order })
  return response.data
}

// =============================================================================
// Test Connection API
// =============================================================================

export interface AITestResponse {
  success: boolean
  provider: string
  model: string
  responseText: string | null
  tokensIn: number | null
  tokensOut: number | null
  latencyMs: number | null
  error: string | null
}

export async function testAIProvider(
  provider: string,
  prompt?: string
): Promise<AITestResponse> {
  const response = await api.post('/admin/ai-suite/test', {
    provider,
    ...(prompt ? { prompt } : {}),
  })
  return response.data
}

// =============================================================================
// Activity Log API
// =============================================================================

export async function fetchGlobalActivity(params: {
  page?: number
  page_size?: number
  provider?: string
}): Promise<AIActivityLogListResponse> {
  const response = await api.get('/admin/ai-suite/activity', { params })
  return response.data
}

export async function fetchStudyAIActivity(
  studyId: string,
  params: { page?: number; page_size?: number }
): Promise<AIActivityLogListResponse> {
  const response = await api.get(`/studies/${studyId}/ai/activity`, { params })
  return response.data
}

// =============================================================================
// AI Capabilities API
// =============================================================================

export interface AIAudienceDefinition {
  key: string
  label: string
  description: string
}

export interface AICapabilityDefinition {
  key: string
  label: string
  description: string
  icon: string
  audiences: AIAudienceDefinition[]
}

export interface AICapabilitiesResponse {
  capabilities: AICapabilityDefinition[]
}

export async function fetchAICapabilities(
  studyId: string
): Promise<AICapabilitiesResponse> {
  const response = await api.get(`/studies/${studyId}/ai/capabilities`)
  return response.data
}

// =============================================================================
// Slide Generation API
// =============================================================================

export async function triggerSlideGeneration(
  studyId: string,
  meetingType: string,
  options?: { brandKitKey?: string; templateKey?: string; pageKey?: string }
): Promise<SlideGenerationTaskResponse> {
  const response = await api.post(`/studies/${studyId}/ai/slides`, {
    meeting_type: meetingType,
    ...(options?.pageKey ? { page_key: options.pageKey } : {}),
    ...(options?.brandKitKey ? { brand_kit_key: options.brandKitKey } : {}),
    ...(options?.templateKey ? { template_key: options.templateKey } : {}),
  })
  return response.data
}

export async function getSlideStatus(
  studyId: string,
  taskId: string
): Promise<SlideStatusResponse> {
  const response = await api.get(
    `/studies/${studyId}/ai/slides/${taskId}/status`
  )
  return response.data
}

export async function downloadSlides(
  studyId: string,
  taskId: string
): Promise<void> {
  const response = await api.get(
    `/studies/${studyId}/ai/slides/${taskId}/download`,
    { responseType: 'blob' }
  )

  // Create download link
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `slides.pptx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
